import { useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useLanguage } from '@/i18n/LanguageContext'
import {
  Workflow, Plus, RefreshCw, Loader2, X, FolderOpen, ChevronDown,
  Unlink, Link2, Check, Zap, Clock, ToggleLeft, ToggleRight,
  Search, Trash2, Cookie, UserPlus, Layers, FileText,
  MessageSquare, Users, AlertTriangle, ClipboardList, Image, Sparkles, Database,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { NeuralGraph } from './NeuralGraph'
import type { ProfileNode, PageNode, MediaNode, Connection, GraphNode } from './NeuralGraph'
import { AddProfileModal } from './AddProfileModal'
import { StatusPanel } from './StatusPanel'
import { DatabaseTab } from './DatabaseTab'
import { GroupsGridTab } from './GroupsGridTab'
import { GroupsCatalogTab } from './GroupsCatalogTab'
import { PostsTab } from './PostsTab'
import { InvitesTab } from './InvitesTab'
import { CommentsTab } from './CommentsTab'
import { ErrorsTab } from './ErrorsTab'
import { StoriesTab } from './StoriesTab'
import { CreationTab } from './CreationTab'
import { toast } from 'sonner'

/* ------------------------------------------------------------------ */
/*  API types                                                          */
/* ------------------------------------------------------------------ */

interface ApiProfile {
  id: string
  name: string
  fb_email?: string | null
  fb_password?: string | null
  fb_user_id: string | null
  proxy?: string | null
  status: string | null
  discovered_pages_json: string[] | null
  connections_count: number
  automation_enabled: boolean
  post_interval_hours: number
  double_story: boolean
  next_run_at: number | null
  last_heartbeat_at: number | null
  created_at: number
}

interface ApiConnection {
  id: string
  agent_profile_id: string
  agent_profile_name: string
  agent_profile_status: string
  fb_page_name: string
  fb_page_id: string | null
  media_folder_id: string | null
  media_folder_name: string | null
  story_link: string | null
  stories_posted: number
  last_story_at: number | null
  invites_sent: number
  last_invite_at: number | null
  created_at: number
}

interface ApiMediaFolder {
  id: string
  name: string
  images_count: number
  preview_urls?: string[]
}

interface ImportPreviewEntry {
  fb_page_name: string
  fb_page_id: string | null
  groups_count?: number
  profiles: Array<{
    agent_profile_id: string
    profile_name: string
    status: 'new' | 'duplicate'
  }>
}

interface ImportPreviewResponse {
  ok: boolean
  entries: ImportPreviewEntry[]
  summary: {
    profiles: number
    parsed: number
    valid: number
    ignored: number
    new_pairs: number
    duplicate_pairs: number
    group_links_matched?: number
  }
}

/* ------------------------------------------------------------------ */
/*  Tab definitions                                                    */
/* ------------------------------------------------------------------ */

type TabId = 'overview' | 'database' | 'catalog' | 'groups' | 'posts' | 'stories' | 'invites' | 'comments' | 'errors' | 'creation'

const TABS: { id: TabId; label: string; icon: typeof Workflow }[] = [
  { id: 'overview', label: 'Visao Geral', icon: Workflow },
  { id: 'database', label: 'Banco de Contas', icon: Database },
  { id: 'catalog', label: 'Cadastro', icon: ClipboardList },
  { id: 'groups', label: 'Config Grupos', icon: Layers },
  { id: 'posts', label: 'Postagens', icon: FileText },
  { id: 'stories', label: 'Stories', icon: Image },
  { id: 'invites', label: 'Convites', icon: Users },
  { id: 'comments', label: 'Comentarios', icon: MessageSquare },
  { id: 'creation', label: 'Criacao', icon: Sparkles },
  { id: 'errors', label: 'Erros', icon: AlertTriangle },
]

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */

export function AutomacaoFB() {
  const { t } = useLanguage()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectedPage, setSelectedPage] = useState<{ name: string; pageId: string } | null>(null)
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false)
  const [folderSearch, setFolderSearch] = useState('')
  const [storyLinkDraft, setStoryLinkDraft] = useState('')
  const [editCookiesOpen, setEditCookiesOpen] = useState(false)
  const [cookiesDraft, setCookiesDraft] = useState('')
  const [manualPageModalOpen, setManualPageModalOpen] = useState(false)
  const [manualPageName, setManualPageName] = useState('')
  const [manualPageId, setManualPageId] = useState('')
  const [bulkPagesDraft, setBulkPagesDraft] = useState('')
  const [bulkGroupLinksDraft, setBulkGroupLinksDraft] = useState('')
  const [importProfileIds, setImportProfileIds] = useState<string[]>([])
  const [importPreview, setImportPreview] = useState<ImportPreviewResponse | null>(null)
  const [storyPageSearch, setStoryPageSearch] = useState('')
  const [storyPageDropdownOpen, setStoryPageDropdownOpen] = useState(false)
  const [bulkStoryLinkDraft, setBulkStoryLinkDraft] = useState('')
  const [bulkUnlinkMode, setBulkUnlinkMode] = useState(false)
  const [bulkUnlinkPageIds, setBulkUnlinkPageIds] = useState<Set<string>>(new Set())

  // ── Fetch profiles ──
  const {
    data: profilesData,
    isLoading: loadingProfiles,
    refetch: refetchProfiles,
  } = useQuery<{ profiles: ApiProfile[] }>({
    queryKey: ['automacao-fb', 'profiles'],
    queryFn: () => api.get('/automacao-fb/profiles'),
    refetchInterval: 30000,
  })

  // ── Fetch connections ──
  const {
    data: connectionsData,
    isLoading: loadingConnections,
    refetch: refetchConnections,
  } = useQuery<{ connections: ApiConnection[] }>({
    queryKey: ['automacao-fb', 'connections'],
    queryFn: () => api.get('/automacao-fb/connections'),
    refetchInterval: 30000,
  })

  // ── Fetch media folders (only when sidebar is open) ──
  const { data: foldersData } = useQuery<ApiMediaFolder[]>({
    queryKey: ['media-folders'],
    queryFn: () => api.get('/media-folders'),
    enabled: !!selectedPage,
  })

  // ── Mutations ──
  const updateConn = useMutation({
    mutationFn: ({ connectionId, data }: { connectionId: string; data: Record<string, unknown> }) =>
      api.put(`/automacao-fb/connections/${connectionId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automacao-fb', 'connections'] }),
    onError: () => toast.error('Erro ao atualizar'),
  })

  const updateProfile = useMutation({
    mutationFn: ({ profileId, data }: { profileId: string; data: Record<string, unknown> }) =>
      api.put(`/automacao-fb/profiles/${profileId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automacao-fb', 'profiles'] }),
    onError: () => toast.error('Erro ao atualizar perfil'),
  })

  const deleteProfile = useMutation({
    mutationFn: (profileId: string) => api.delete(`/automacao-fb/profiles/${profileId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb'] })
      setSelectedProfileId(null)
      toast.success('Perfil removido')
    },
    onError: () => toast.error('Erro ao remover perfil'),
  })

  const deleteConnection = useMutation({
    mutationFn: (connectionId: string) => api.delete(`/automacao-fb/connections/${connectionId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automacao-fb', 'connections'] }),
    onError: () => toast.error('Erro ao desvincular'),
  })

  const createConnection = useMutation({
    mutationFn: (data: { agent_profile_id: string; fb_page_name: string; fb_page_id?: string }) =>
      api.post<{ ok: boolean; connection?: { duplicate?: boolean } }>('/automacao-fb/connections', data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'connections'] })
      toast.success(res?.connection?.duplicate ? 'Pagina ja estava vinculada' : 'Pagina adicionada')
      setManualPageName('')
      setManualPageId('')
      setManualPageModalOpen(false)
    },
    onError: () => toast.error('Erro ao cadastrar pagina'),
  })

  const importConnections = useMutation({
    mutationFn: (data: { agent_profile_ids: string[]; lines: string; group_links_lines?: string }) =>
      api.post<{ ok: boolean; created: number; duplicates: number; ignored: number; profiles: number; groups_created?: number; groups_duplicates?: number }>('/automacao-fb/connections/import', data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'connections'] })
      const parts = [`${res?.created ?? 0} novas`]
      if ((res?.duplicates ?? 0) > 0) parts.push(`${res?.duplicates} repetidas`)
      if ((res?.ignored ?? 0) > 0) parts.push(`${res?.ignored} ignoradas`)
      if ((res?.groups_created ?? 0) > 0) parts.push(`${res?.groups_created} grupos criados`)
      if ((res?.groups_duplicates ?? 0) > 0) parts.push(`${res?.groups_duplicates} grupos repetidos`)
      toast.success(`Importacao concluida em ${res?.profiles ?? importProfileIds.length} perfil(is): ${parts.join(' · ')}`)
      setBulkPagesDraft('')
      setBulkGroupLinksDraft('')
      setImportPreview(null)
      setManualPageModalOpen(false)
    },
    onError: () => toast.error('Erro ao importar lista de paginas'),
  })

  const bulkStoryLink = useMutation({
    mutationFn: (storyLink: string) =>
      api.put<{ ok: boolean; updated: number }>('/automacao-fb/connections/bulk-story-link', { story_link: storyLink }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'connections'] })
      toast.success(`Link atualizado em ${res?.updated ?? 0} conexoes`)
    },
    onError: () => toast.error('Erro ao atualizar link em massa'),
  })

  const previewImport = useMutation({
    mutationFn: (data: { agent_profile_ids: string[]; lines: string; group_links_lines?: string }) =>
      api.post<ImportPreviewResponse>('/automacao-fb/connections/import-preview', data),
    onSuccess: (res) => {
      setImportPreview(res)
      toast.success('Preview atualizado')
    },
    onError: () => toast.error('Erro ao gerar preview'),
  })

  const isLoading = loadingProfiles || loadingConnections
  const apiProfiles = profilesData?.profiles ?? []
  const apiConnections = connectionsData?.connections ?? []
  const mediaFolders: ApiMediaFolder[] = useMemo(() => {
    const raw = Array.isArray(foldersData) ? foldersData : []
    return raw.map((folder) => {
      const images = Array.isArray((folder as ApiMediaFolder & { images?: Array<{ mediaType?: string; url?: string }> }).images)
        ? (folder as ApiMediaFolder & { images?: Array<{ mediaType?: string; url?: string }> }).images ?? []
        : []

      return {
        id: folder.id,
        name: folder.name,
        images_count: images.length || folder.images_count || 0,
        preview_urls: folder.preview_urls ?? images
          .filter((image) => image.mediaType === 'image' && image.url)
          .slice(0, 4)
          .map((image) => image.url as string),
      }
    })
  }, [foldersData])

  // ── Graph data ──
  const profiles: ProfileNode[] = useMemo(
    () =>
      apiProfiles.map((p) => ({
        id: p.id,
        name: p.name,
        type: 'profile' as const,
        status: (p.status === 'online' ? 'active' : p.status === 'suspended' ? 'suspended' : p.status === 'error' ? 'error' : 'idle') as ProfileNode['status'],
        pagesCount: p.connections_count,
      })),
    [apiProfiles],
  )

  const pages: PageNode[] = useMemo(() => {
    const seen = new Map<string, PageNode>()
    for (const c of apiConnections) {
      const key = c.fb_page_name.toLowerCase()
      if (!seen.has(key)) {
        seen.set(key, {
          id: `page-${c.fb_page_id || c.fb_page_name}`,
          name: c.fb_page_name,
          type: 'page',
          profileId: c.agent_profile_id,
        })
      }
    }
    return Array.from(seen.values())
  }, [apiConnections])

  const media: MediaNode[] = useMemo(() => {
    const seen = new Map<string, MediaNode>()
    for (const c of apiConnections) {
      if (c.media_folder_id && c.media_folder_name) {
        if (!seen.has(c.media_folder_id)) {
          seen.set(c.media_folder_id, {
            id: `media-${c.media_folder_id}`,
            name: c.media_folder_name,
            type: 'media',
            pageId: `page-${c.fb_page_id || c.fb_page_name}`,
          })
        }
      }
    }
    return Array.from(seen.values())
  }, [apiConnections])

  const connections: Connection[] = useMemo(() => {
    const result: Connection[] = []
    for (const c of apiConnections) {
      const pageId = `page-${c.fb_page_id || c.fb_page_name}`
      result.push({ id: `conn-${c.id}`, sourceId: c.agent_profile_id, targetId: pageId, type: 'profile-page' })
      if (c.media_folder_id) {
        result.push({ id: `media-conn-${c.id}`, sourceId: pageId, targetId: `media-${c.media_folder_id}`, type: 'page-media' })
      }
    }
    return result
  }, [apiConnections])

  const activeCount = profiles.filter((p) => p.status === 'active').length

  const handleRefresh = () => { refetchProfiles(); refetchConnections() }

  const handleNodeClick = useCallback((node: GraphNode) => {
    if (node.type === 'page') {
      if (bulkUnlinkMode) {
        // Toggle page in bulk selection
        setBulkUnlinkPageIds(prev => {
          const next = new Set(prev)
          if (next.has(node.id)) next.delete(node.id)
          else next.add(node.id)
          return next
        })
        return
      }
      setSelectedPage({ name: node.name, pageId: node.id })
      setSelectedProfileId(null)
      setFolderDropdownOpen(false)
      setStoryPageSearch('')
      setStoryPageDropdownOpen(false)
    } else if (node.type === 'profile') {
      setSelectedProfileId(node.id)
      setSelectedPage(null)
      setBulkUnlinkMode(false)
      setBulkUnlinkPageIds(new Set())
    }
  }, [bulkUnlinkMode])

  const selectedPageConnections = useMemo(() => {
    if (!selectedPage) return []
    return apiConnections.filter(c => `page-${c.fb_page_id || c.fb_page_name}` === selectedPage.pageId)
  }, [selectedPage, apiConnections])

  const selectedProfile = useMemo(
    () => selectedProfileId ? apiProfiles.find(p => p.id === selectedProfileId) ?? null : null,
    [selectedProfileId, apiProfiles],
  )

  const filteredStoryPages = useMemo(() => {
    const query = storyPageSearch.trim().toLowerCase()
    if (!query) return pages.slice(0, 12)
    return pages.filter((page) => page.name.toLowerCase().includes(query)).slice(0, 12)
  }, [pages, storyPageSearch])

  const currentFolderId = selectedPageConnections[0]?.media_folder_id ?? null
  const currentFolderName = selectedPageConnections[0]?.media_folder_name ?? null
  const currentStoryLink = selectedPageConnections[0]?.story_link ?? ''

  useEffect(() => {
    setStoryLinkDraft(currentStoryLink)
  }, [currentStoryLink, selectedPage?.pageId])

  const handleSelectFolder = (folderId: string | null) => {
    setFolderDropdownOpen(false)
    setFolderSearch('')
    for (const conn of selectedPageConnections) {
      updateConn.mutate({ connectionId: conn.id, data: { media_folder_id: folderId } })
    }
  }

  const handleSaveStoryLink = (link: string) => {
    for (const conn of selectedPageConnections) {
      updateConn.mutate({ connectionId: conn.id, data: { story_link: link } })
    }
    toast.success('Link salvo')
  }

  const handleUnlinkPage = () => {
    if (!selectedPage || selectedPageConnections.length === 0) return
    const count = selectedPageConnections.length
    if (!confirm(`Desvincular a pagina "${selectedPage.name}" de ${count} perfil(is)?\n\nA pagina sai do grafo e para de postar. Voce pode re-vincular depois rodando "Descobrir paginas" no perfil.`)) return
    let remaining = count
    for (const conn of selectedPageConnections) {
      deleteConnection.mutate(conn.id, {
        onSuccess: () => {
          remaining -= 1
          if (remaining === 0) {
            toast.success('Pagina desvinculada')
            setSelectedPage(null)
          }
        },
      })
    }
  }

  const handleUnlinkProfileFromPage = (connectionId: string, profileName: string) => {
    if (!confirm(`Remover "${profileName}" desta pagina?`)) return
    deleteConnection.mutate(connectionId, {
      onSuccess: () => {
        toast.success('Perfil removido da pagina')
        // Se essa era a ultima conexao, fecha o sidebar
        if (selectedPageConnections.length === 1) setSelectedPage(null)
      },
    })
  }

  const handleBulkUnlink = () => {
    if (!selectedProfileId || bulkUnlinkPageIds.size === 0) return
    const count = bulkUnlinkPageIds.size
    if (!confirm(`Desvincular ${count} página(s) deste perfil?`)) return

    const connIds = apiConnections
      .filter(c => c.agent_profile_id === selectedProfileId && bulkUnlinkPageIds.has(`page-${c.fb_page_id || c.fb_page_name}`))
      .map(c => c.id)

    let remaining = connIds.length
    for (const connId of connIds) {
      deleteConnection.mutate(connId, {
        onSuccess: () => {
          remaining -= 1
          if (remaining === 0) {
            toast.success(`${count} página(s) desvinculada(s)`)
            setBulkUnlinkPageIds(new Set())
            setBulkUnlinkMode(false)
          }
        },
      })
    }
  }

  const toggleImportProfile = (profileId: string) => {
    setImportProfileIds((current) => (
      current.includes(profileId)
        ? current.filter((id) => id !== profileId)
        : [...current, profileId]
    ))
    setImportPreview(null)
  }

  const handleCsvUpload = async (file: File | null) => {
    if (!file) return
    const text = await file.text()
    setBulkPagesDraft(text)
    setImportPreview(null)
    toast.success(`CSV carregado: ${file.name}`)
  }

  // Close sidebars when switching tabs
  useEffect(() => {
    if (activeTab !== 'overview') {
      setSelectedPage(null)
      setSelectedProfileId(null)
      setStoryPageSearch('')
      setStoryPageDropdownOpen(false)
    }
  }, [activeTab])

  return (
    <>
      <div
        className="-mx-6 -mt-6 -mb-6 md:-mx-8 md:-mt-8 md:-mb-8 overflow-hidden"
        style={{ height: 'calc(100vh - 56px)' }}
      >
        <div className="relative flex flex-col bg-background text-foreground h-full">
          {/* ── Header bar ── */}
          <div className="relative z-30 flex items-center justify-between px-6 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                <Workflow className="h-4.5 w-4.5 text-indigo-400" />
              </div>
              <div>
                <h1 className="text-base font-bold tracking-tight text-foreground">Automacao FB</h1>
                <p className="text-[10px] text-muted-foreground">
                  {profiles.length} perfis &middot; {pages.length} paginas &middot; {media.length} midias
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                className="gap-1.5 rounded-lg border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Atualizar
              </Button>
              <Button
                size="sm"
                onClick={() => setAddModalOpen(true)}
                className="gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Plus className="h-3.5 w-3.5" />
                Perfil
              </Button>
            </div>
          </div>

          {/* ── Tab bar ── */}
          <div className="relative z-30 flex items-center gap-1 px-6 py-0 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            {TABS.map(tab => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-colors relative ${
                    isActive
                      ? 'text-indigo-400'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                  {isActive && (
                    <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-indigo-500 rounded-t" />
                  )}
                </button>
              )
            })}
          </div>

          {/* ── Tab content ── */}
          <div className="relative flex-1 min-h-0">
            {/* ═══ Overview tab ═══ */}
            {activeTab === 'overview' && (
              <>
                {/* ── Bulk story link bar ── */}
                {apiConnections.length > 0 && (
                  <div className="absolute top-0 left-0 right-0 z-20 flex items-center gap-2 px-4 py-2 bg-background/90 backdrop-blur border-b border-border">
                    <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Link do Story (todas):</span>
                    <input
                      type="url"
                      value={bulkStoryLinkDraft}
                      onChange={(e) => setBulkStoryLinkDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && bulkStoryLinkDraft.trim()) {
                          bulkStoryLink.mutate(bulkStoryLinkDraft.trim())
                        }
                      }}
                      placeholder="https://exemplo.com — aplica em todas as paginas"
                      className="flex-1 min-w-0 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none transition-colors"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (!bulkStoryLinkDraft.trim()) return
                        bulkStoryLink.mutate(bulkStoryLinkDraft.trim())
                      }}
                      disabled={!bulkStoryLinkDraft.trim() || bulkStoryLink.isPending}
                      className="gap-1.5 text-xs h-7 shrink-0"
                    >
                      {bulkStoryLink.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      Aplicar em todas
                    </Button>
                  </div>
                )}

                {isLoading && profiles.length === 0 ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-indigo-400 mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">Carregando grafo...</p>
                    </div>
                  </div>
                ) : (
                  <NeuralGraph
                    profiles={profiles}
                    pages={pages}
                    media={media}
                    connections={connections}
                    selectedProfileId={selectedProfileId}
                    bulkSelectedPageIds={bulkUnlinkMode ? bulkUnlinkPageIds : undefined}
                    onNodeClick={handleNodeClick}
                  />
                )}

                {(profiles.length > 0 || pages.length > 0) && (
                  <StatusPanel
                    profileCount={profiles.length}
                    pageCount={pages.length}
                    mediaCount={media.length}
                    connectionCount={connections.length}
                    activeCount={activeCount}
                  />
                )}

                {/* ── Bulk unlink bar ── */}
                {bulkUnlinkMode && selectedProfileId && (
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-lg bg-[#18181b]/95 border border-red-500/30 backdrop-blur-md px-4 py-2.5 shadow-2xl">
                    <span className="text-xs text-zinc-300">
                      {bulkUnlinkPageIds.size === 0
                        ? 'Clique nas páginas para selecionar'
                        : `${bulkUnlinkPageIds.size} página(s) selecionada(s)`}
                    </span>
                    <button
                      onClick={handleBulkUnlink}
                      disabled={bulkUnlinkPageIds.size === 0 || deleteConnection.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 text-xs text-white font-medium hover:bg-red-700 disabled:opacity-30 transition-colors"
                    >
                      {deleteConnection.isPending
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Unlink className="h-3 w-3" />}
                      Desvincular
                    </button>
                    <button
                      onClick={() => { setBulkUnlinkMode(false); setBulkUnlinkPageIds(new Set()) }}
                      className="flex items-center justify-center h-7 w-7 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* ── Page detail sidebar ── */}
                {selectedPage && (
                  <div className="absolute top-0 right-0 bottom-0 z-30 w-72 bg-card/95 text-card-foreground backdrop-blur-md border-l border-border flex flex-col">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground truncate">{selectedPage.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Pagina do Facebook</p>
                      </div>
                      <button
                        onClick={() => setSelectedPage(null)}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="px-4 py-3 border-b border-border bg-background/60">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <input
                          type="text"
                          value={storyPageSearch}
                          onFocus={() => setStoryPageDropdownOpen(true)}
                          onChange={(e) => {
                            setStoryPageSearch(e.target.value)
                            setStoryPageDropdownOpen(true)
                          }}
                          placeholder="Buscar pagina para configurar..."
                          className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none"
                        />
                        {storyPageDropdownOpen && (
                          <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-64 overflow-y-auto rounded-xl border border-border bg-popover shadow-xl">
                            {filteredStoryPages.length === 0 ? (
                              <p className="px-3 py-3 text-[11px] text-muted-foreground text-center">Nenhuma pagina encontrada</p>
                            ) : (
                              filteredStoryPages.map((page) => (
                                <button
                                  key={page.id}
                                  onClick={() => {
                                    setSelectedPage({ name: page.name, pageId: page.id })
                                    setStoryPageSearch(page.name)
                                    setStoryPageDropdownOpen(false)
                                    setFolderDropdownOpen(false)
                                  }}
                                  className={`block w-full border-b border-border px-3 py-2.5 text-left text-xs last:border-0 ${
                                    selectedPage.pageId === page.id
                                      ? 'bg-primary/10 text-foreground'
                                      : 'text-foreground/85 hover:bg-muted hover:text-foreground'
                                  }`}
                                >
                                  <p className="truncate font-medium">{page.name}</p>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {/* Stats */}
                      {(() => {
                        const totalStories = selectedPageConnections.reduce((sum, c) => sum + (c.stories_posted || 0), 0)
                        const lastStoryAt = selectedPageConnections.reduce((max, c) => Math.max(max, c.last_story_at || 0), 0)
                        const totalInvites = selectedPageConnections.reduce((sum, c) => sum + (c.invites_sent || 0), 0)
                        const lastInviteAt = selectedPageConnections.reduce((max, c) => Math.max(max, c.last_invite_at || 0), 0)
                        if (totalStories === 0 && totalInvites === 0) return null
                        return (
                          <div className="space-y-1.5">
                            {totalStories > 0 && (
                              <div className="flex items-center justify-between px-3 py-2 rounded-md bg-emerald-500/5 border border-emerald-500/10">
                                <div>
                                  <p className="text-xs font-semibold text-emerald-300">{totalStories} stories</p>
                                  {lastStoryAt > 0 && (
                                      <p className="text-[10px] text-muted-foreground">
                                      Ultimo: {new Date(lastStoryAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                            {totalInvites > 0 && (
                              <div className="flex items-center justify-between px-3 py-2 rounded-md bg-indigo-500/5 border border-indigo-500/10">
                                <div>
                                  <p className="text-xs font-semibold text-indigo-300">{totalInvites} convites</p>
                                  {lastInviteAt > 0 && (
                                      <p className="text-[10px] text-muted-foreground">
                                      Ultimo: {new Date(lastInviteAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })()}

                      {/* Connected profiles */}
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Perfis conectados</p>
                        <div className="space-y-1.5">
                          {selectedPageConnections.map((c) => (
                            <div key={c.id} className="group flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/50">
                              <div className={`h-2 w-2 rounded-full ${c.agent_profile_status === 'online' ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                              <span className="flex-1 text-xs text-foreground/85 truncate">{c.agent_profile_name}</span>
                              <button
                                onClick={() => handleUnlinkProfileFromPage(c.id, c.agent_profile_name)}
                                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-all shrink-0"
                                title="Remover este perfil da pagina"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Media folder */}
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Pasta de midia</p>
                        {currentFolderId ? (() => {
                          const currentFolder = mediaFolders.find(f => f.id === currentFolderId)
                          return (
                            <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3.5 py-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/15 shrink-0">
                                <FolderOpen className="h-4 w-4 text-emerald-400" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-emerald-300 leading-tight">{currentFolderName}</p>
                                <p className="mt-0.5 text-[11px] text-emerald-600">{currentFolder?.images_count ?? 0} imagens</p>
                              </div>
                              {currentFolder?.preview_urls && currentFolder.preview_urls.length > 0 && (
                                <div className="flex -space-x-2 shrink-0">
                                  {currentFolder.preview_urls.slice(0, 4).map((url, i) => (
                                    <img key={i} src={url} className="h-9 w-9 rounded-xl object-cover border-2 border-[#111113]" />
                                  ))}
                                </div>
                              )}
                              <button
                                onClick={() => handleSelectFolder(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500 shrink-0"
                                title="Desvincular"
                              >
                                <Unlink className="h-4 w-4" />
                              </button>
                            </div>
                          )
                        })() : (
                          <div className="rounded-xl border border-dashed border-border bg-muted/30 px-3.5 py-3">
                            <p className="text-sm font-medium text-foreground">Nenhuma pasta vinculada</p>
                            <p className="mt-1 text-[11px] text-muted-foreground">Escolha uma pasta para essa pagina postar stories com a midia certa.</p>
                          </div>
                        )}
                        <div className="relative mt-2">
                          <button
                            onClick={() => { setFolderDropdownOpen(!folderDropdownOpen); if (folderDropdownOpen) setFolderSearch('') }}
                            className="flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                          >
                            <span>{currentFolderId ? 'Trocar pasta' : 'Selecionar pasta'}</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${folderDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {folderDropdownOpen && (
                            <div className="absolute left-0 right-0 z-10 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-border bg-popover p-2 text-popover-foreground shadow-xl">
                              <div className="relative mb-2">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                <input
                                  type="text"
                                  placeholder="Buscar pasta..."
                                  value={folderSearch}
                                  onChange={(e) => setFolderSearch(e.target.value)}
                                  className="w-full rounded-lg border border-border bg-background py-2 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                                  autoFocus
                                />
                              </div>
                              {(() => {
                                const filtered = mediaFolders.filter((f) =>
                                  f.name.toLowerCase().includes(folderSearch.toLowerCase())
                                )
                                if (filtered.length === 0) {
                                  return <p className="px-3 py-3 text-[11px] text-muted-foreground">Nenhuma pasta encontrada</p>
                                }
                                return filtered.map((f) => (
                                  <button
                                    key={f.id}
                                    onClick={() => handleSelectFolder(f.id)}
                                    className={`mb-2 flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors last:mb-0 ${
                                      f.id === currentFolderId ? 'border-primary/30 bg-primary/10 text-foreground' : 'border-border text-foreground/80 hover:bg-muted hover:text-foreground'
                                    }`}
                                  >
                                    {f.preview_urls && f.preview_urls.length > 0 ? (
                                      <div className="flex -space-x-2 shrink-0">
                                        {f.preview_urls.slice(0, 4).map((url, i) => (
                                          <img key={i} src={url} className="h-10 w-10 rounded-xl object-cover border-2 border-[#111113]" />
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted shrink-0">
                                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-medium leading-tight">{f.name}</p>
                                      <p className="mt-1 text-[11px] text-muted-foreground">{f.images_count} imagens</p>
                                    </div>
                                  </button>
                                ))
                              })()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Story link */}
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Link do Story</p>
                        <div className="flex gap-1.5">
                          <div className="relative flex-1">
                            <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <input
                              type="url"
                              value={storyLinkDraft}
                              onChange={(e) => setStoryLinkDraft(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveStoryLink(storyLinkDraft) }}
                              placeholder="https://exemplo.com"
                              className="w-full rounded-md border border-border bg-background pl-8 pr-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none transition-colors"
                            />
                          </div>
                          <button
                            onClick={() => handleSaveStoryLink(storyLinkDraft)}
                            disabled={storyLinkDraft === currentStoryLink}
                            className="flex h-[34px] w-[34px] items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:text-emerald-600 hover:border-emerald-500/30 disabled:opacity-30 disabled:cursor-default transition-colors shrink-0"
                            title="Salvar link"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {currentStoryLink && (
                          <p className="text-[10px] text-muted-foreground mt-1.5 truncate" title={currentStoryLink}>
                            Atual: {currentStoryLink}
                          </p>
                        )}
                      </div>

                      {/* Quick links to tabs */}
                      <div className="border-t border-border pt-3 space-y-1.5">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Gerenciar</p>
                        <button
                          onClick={() => setActiveTab('groups')}
                          className="flex w-full items-center gap-2 px-3 py-2 rounded-md border border-border bg-background text-xs text-foreground/85 hover:text-primary hover:border-primary/30 hover:bg-muted transition-colors"
                        >
                          <Layers className="h-3.5 w-3.5" /> Grupos
                        </button>
                        <button
                          onClick={() => setActiveTab('comments')}
                          className="flex w-full items-center gap-2 px-3 py-2 rounded-md border border-[#27272a] bg-[#111113] text-xs text-zinc-400 hover:text-indigo-300 hover:border-indigo-500/30 transition-colors"
                        >
                          <MessageSquare className="h-3.5 w-3.5" /> Comentarios
                        </button>
                        <button
                          onClick={() => setActiveTab('invites')}
                          className="flex w-full items-center gap-2 px-3 py-2 rounded-md border border-[#27272a] bg-[#111113] text-xs text-zinc-400 hover:text-indigo-300 hover:border-indigo-500/30 transition-colors"
                        >
                          <Users className="h-3.5 w-3.5" /> Convites
                        </button>
                      </div>

                      {/* Danger zone: unlink page */}
                      <div className="border-t border-border pt-3">
                        <button
                          onClick={handleUnlinkPage}
                          disabled={deleteConnection.isPending}
                          className="flex w-full items-center justify-center gap-2 px-3 py-2 rounded-md border border-red-500/20 bg-red-500/5 text-xs text-red-400 hover:bg-red-500/10 hover:border-red-500/30 disabled:opacity-50 transition-colors"
                          title="Remove todas as conexoes desta pagina"
                        >
                          <Unlink className="h-3.5 w-3.5" /> Desvincular pagina
                        </button>
                        <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
                          Remove a pagina do grafo. Voce pode re-vincular depois rodando "Descobrir paginas" no perfil.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Profile detail sidebar ── */}
                {selectedProfile && (
                  <div className="absolute top-0 right-0 bottom-0 z-30 w-72 bg-card/95 text-card-foreground backdrop-blur-md border-l border-border flex flex-col">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground truncate">{selectedProfile.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Perfil — {selectedProfile.connections_count} paginas</p>
                      </div>
                      <button
                        onClick={() => setSelectedProfileId(null)}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {/* Suspended account banner */}
                      {selectedProfile.status === 'suspended' && (
                        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                              <X className="h-2.5 w-2.5 text-white" />
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-red-400">Conta Suspensa</p>
                              <p className="text-[10px] text-red-300/70 mt-0.5">
                                O Facebook suspendeu esta conta. Verifique manualmente e faca a apelacao antes de reativar.
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              updateProfile.mutate({ profileId: selectedProfile.id, data: { status: 'idle' } })
                              toast.success('Perfil marcado como resolvido')
                            }}
                            className="w-full text-center text-[11px] font-medium text-red-300 hover:text-white bg-red-500/20 hover:bg-red-500/30 rounded-md py-1.5 transition-colors"
                          >
                            Marcar como Resolvido
                          </button>
                        </div>
                      )}

                      {/* Automation toggle */}
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Automacao</p>
                        <button
                          onClick={() => updateProfile.mutate({
                            profileId: selectedProfile.id,
                            data: { automation_enabled: !selectedProfile.automation_enabled },
                          })}
                          className={`flex w-full items-center justify-between px-3 py-2.5 rounded-md border transition-colors ${
                            selectedProfile.automation_enabled
                              ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300'
                              : 'bg-muted/50 border-border text-muted-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Zap className="h-3.5 w-3.5" />
                            <span className="text-xs font-medium">
                              {selectedProfile.automation_enabled ? 'Ativa' : 'Desativada'}
                            </span>
                          </div>
                          {selectedProfile.automation_enabled
                            ? <ToggleRight className="h-5 w-5 text-emerald-400" />
                            : <ToggleLeft className="h-5 w-5" />
                          }
                        </button>
                      </div>

                      {/* Interval */}
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Intervalo</p>
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-[11px] text-muted-foreground">Postar a cada</span>
                          <input
                            type="number"
                            min={1}
                            max={72}
                            value={selectedProfile.post_interval_hours}
                            onChange={(e) => {
                              const val = Math.max(1, Math.min(72, parseInt(e.target.value) || 8))
                              updateProfile.mutate({ profileId: selectedProfile.id, data: { post_interval_hours: val } })
                            }}
                            className="w-14 rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground text-center focus:border-primary/50 focus:outline-none"
                          />
                          <span className="text-[11px] text-muted-foreground">horas</span>
                        </div>
                      </div>

                      {/* Double story toggle */}
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Modo de postagem</p>
                        <button
                          onClick={() => updateProfile.mutate({
                            profileId: selectedProfile.id,
                            data: { double_story: !selectedProfile.double_story },
                          })}
                          className={`flex w-full items-center justify-between px-3 py-2.5 rounded-md border transition-colors ${
                            selectedProfile.double_story
                              ? 'bg-indigo-500/5 border-indigo-500/20 text-indigo-300'
                              : 'bg-muted/50 border-border text-muted-foreground'
                          }`}
                        >
                          <span className="text-xs font-medium">
                            {selectedProfile.double_story ? '2 stories (imagem + link)' : '1 story (com link)'}
                          </span>
                          {selectedProfile.double_story
                            ? <ToggleRight className="h-5 w-5 text-indigo-400" />
                            : <ToggleLeft className="h-5 w-5" />
                          }
                        </button>
                        <p className="text-[10px] text-muted-foreground mt-1.5">
                          {selectedProfile.double_story
                            ? '1o story so imagem, 2o com link — melhor alcance'
                            : 'Posta direto com link'}
                        </p>
                      </div>

                      {/* Next run */}
                      {selectedProfile.next_run_at && (
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Proximo ciclo</p>
                          <p className="text-xs text-foreground/85">
                            {new Date(selectedProfile.next_run_at).toLocaleString('pt-BR', {
                              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                            })}
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="border-t border-border pt-3 space-y-2">
                        <button
                          onClick={() => {
                            setImportProfileIds(selectedProfile ? [selectedProfile.id] : [])
                            setImportPreview(null)
                            setManualPageModalOpen(true)
                          }}
                          className="flex w-full items-center justify-center gap-2 px-3 py-2 rounded-md border border-border bg-background text-xs text-foreground/85 hover:border-primary/30 hover:text-primary hover:bg-muted transition-colors"
                        >
                          <Plus className="h-3.5 w-3.5" /> Adicionar paginas manualmente
                        </button>

                        <button
                          onClick={() => {
                            setBulkUnlinkMode(true)
                            setBulkUnlinkPageIds(new Set())
                          }}
                          className="flex w-full items-center justify-center gap-2 px-3 py-2 rounded-md border border-red-500/20 bg-red-500/5 text-xs text-red-400 hover:bg-red-500/10 hover:border-red-500/30 transition-colors"
                        >
                          <Unlink className="h-3.5 w-3.5" /> Desvincular páginas em massa
                        </button>

                        <button
                          onClick={() => toast.info('Rode no terminal: node debug.js invite --profile "' + selectedProfile.name + '"')}
                          className="flex w-full items-center justify-center gap-2 px-3 py-2 rounded-md border border-border bg-background text-xs text-foreground/85 hover:border-emerald-500/30 hover:text-emerald-600 hover:bg-muted transition-colors"
                        >
                          <UserPlus className="h-3.5 w-3.5" /> Convidar de reacoes
                        </button>

                        <button
                          onClick={() => { setEditCookiesOpen(!editCookiesOpen); setCookiesDraft('') }}
                          className="flex w-full items-center justify-center gap-2 px-3 py-2 rounded-md border border-border bg-background text-xs text-foreground/85 hover:border-amber-500/30 hover:text-amber-600 hover:bg-muted transition-colors"
                        >
                          <Cookie className="h-3.5 w-3.5" /> Atualizar cookies
                        </button>

                        {editCookiesOpen && (
                          <div className="space-y-2">
                            <textarea
                              value={cookiesDraft}
                              onChange={(e) => setCookiesDraft(e.target.value)}
                              placeholder="Cole os cookies aqui (JSON)"
                              rows={4}
                              className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-amber-500/50 focus:outline-none resize-none"
                            />
                            <button
                              onClick={() => {
                                if (!cookiesDraft.trim()) return
                                updateProfile.mutate(
                                  { profileId: selectedProfile.id, data: { cookies_json: cookiesDraft.trim() } },
                                  { onSuccess: () => { toast.success('Cookies atualizados'); setEditCookiesOpen(false) } },
                                )
                              }}
                              disabled={!cookiesDraft.trim()}
                              className="flex w-full items-center justify-center gap-2 px-3 py-2 rounded-md bg-amber-600 text-xs text-white font-medium hover:bg-amber-700 disabled:opacity-30 transition-colors"
                            >
                              <Check className="h-3.5 w-3.5" /> Salvar cookies
                            </button>
                          </div>
                        )}

                        <button
                          onClick={() => {
                            if (confirm(`Deletar perfil "${selectedProfile.name}" e todas as conexoes?`)) {
                              deleteProfile.mutate(selectedProfile.id)
                            }
                          }}
                          className="flex w-full items-center justify-center gap-2 px-3 py-2 rounded-md border border-red-500/20 bg-red-500/5 text-xs text-red-400 hover:bg-red-500/10 hover:border-red-500/30 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Deletar perfil
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ═══ Other tabs ═══ */}
            {activeTab !== 'overview' && (
              <div className="h-full overflow-y-auto">
                <div className="max-w-5xl mx-auto px-6 py-6">
                  {activeTab === 'database' && <DatabaseTab profiles={apiProfiles} />}
                  {activeTab === 'catalog' && <GroupsCatalogTab />}
                  {activeTab === 'groups' && <GroupsGridTab />}
                  {activeTab === 'posts' && <PostsTab />}
                  {activeTab === 'stories' && <StoriesTab />}
                  {activeTab === 'invites' && <InvitesTab />}
                  {activeTab === 'comments' && <CommentsTab />}
                  {activeTab === 'creation' && <CreationTab />}
                  {activeTab === 'errors' && <ErrorsTab />}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddProfileModal open={addModalOpen} onOpenChange={setAddModalOpen} />

      <Dialog
        open={manualPageModalOpen}
        onOpenChange={(open) => {
          setManualPageModalOpen(open)
          if (!open) {
            setManualPageName('')
            setManualPageId('')
            setBulkPagesDraft('')
            setImportPreview(null)
            setImportProfileIds(selectedProfile?.id ? [selectedProfile.id] : [])
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adicionar paginas ao perfil</DialogTitle>
            <DialogDescription>
              {selectedProfile
                ? `Cadastre paginas manualmente no perfil ${selectedProfile.name}.`
                : 'Cadastre paginas manualmente.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <section className="space-y-3 rounded-xl border border-border bg-muted/30 p-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Cadastro rapido</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Bom para adicionar uma pagina isolada com nome e, se tiver, o ID do Facebook.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-[1.6fr_1fr]">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">Nome da pagina</label>
                  <input
                    type="text"
                    value={manualPageName}
                    onChange={(e) => setManualPageName(e.target.value)}
                    placeholder="Ex.: Fans Monica Santos"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">ID da pagina</label>
                  <input
                    type="text"
                    value={manualPageId}
                    onChange={(e) => setManualPageId(e.target.value)}
                    placeholder="Opcional"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none"
                  />
                </div>
              </div>

              <Button
                onClick={() => {
                  if (!selectedProfile || !manualPageName.trim()) return
                  createConnection.mutate({
                    agent_profile_id: selectedProfile.id,
                    fb_page_name: manualPageName.trim(),
                    fb_page_id: manualPageId.trim() || undefined,
                  })
                }}
                disabled={!selectedProfile || !manualPageName.trim() || createConnection.isPending}
                className="gap-2"
              >
                {createConnection.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Adicionar pagina
              </Button>
            </section>

            <section className="space-y-3 rounded-xl border border-border bg-muted/30 p-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Importar em lista</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Selecione os perfis de destino e confira o preview antes de gravar. Aceita nome puro, `nome | id`, `id | nome`, link do Facebook ou CSV simples.
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">Perfis que vao receber a importacao</p>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border bg-background p-2 space-y-1.5">
                  {apiProfiles.map((profile) => {
                    const checked = importProfileIds.includes(profile.id)
                    return (
                      <label
                        key={profile.id}
                        className={`flex items-center gap-2 rounded-md px-2 py-2 text-xs cursor-pointer transition-colors ${
                          checked ? 'bg-primary/10 text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleImportProfile(profile.id)}
                          className="h-3.5 w-3.5 rounded border-border"
                        />
                        <span className="flex-1 truncate">{profile.name}</span>
                        {selectedProfile?.id === profile.id && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">Atual</span>
                        )}
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">Importar CSV</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      void handleCsvUpload(e.target.files?.[0] ?? null)
                      e.currentTarget.value = ''
                    }}
                    className="block w-full rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (selectedProfile?.id) {
                      setImportProfileIds([selectedProfile.id])
                      setImportPreview(null)
                    }
                  }}
                  disabled={!selectedProfile}
                >
                  Usar so perfil atual
                </Button>
              </div>

              <Textarea
                value={bulkPagesDraft}
                onChange={(e) => { setBulkPagesDraft(e.target.value); setImportPreview(null) }}
                rows={10}
                placeholder={`Fans Monica Santos\n123456789012345 | Fans Monica Santos\nhttps://facebook.com/fansmonicasantos\n\nOu CSV:\nname,id\nFans Monica Santos,123456789012345`}
                className="min-h-[220px] bg-background"
              />

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">Grupos em massa para todas as paginas (opcional)</label>
                <Textarea
                  value={bulkGroupLinksDraft}
                  onChange={(e) => { setBulkGroupLinksDraft(e.target.value); setImportPreview(null) }}
                  rows={7}
                  placeholder={`https://www.facebook.com/groups/123456789\nGrupo VIP | https://www.facebook.com/groups/grupo.vip\nhttps://www.facebook.com/groups/987654321`}
                  className="min-h-[160px] bg-background"
                />
              </div>

              <div className="rounded-lg border border-border bg-background px-3 py-2 text-[11px] text-muted-foreground leading-relaxed">
                Dica: tudo que voce colar aqui em grupos sera aplicado em massa para todas as paginas importadas, inclusive quando voce selecionar varios perfis de destino.
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (importProfileIds.length === 0 || !bulkPagesDraft.trim()) return
                    previewImport.mutate({
                      agent_profile_ids: importProfileIds,
                      lines: bulkPagesDraft,
                      group_links_lines: bulkGroupLinksDraft,
                    })
                  }}
                  disabled={importProfileIds.length === 0 || !bulkPagesDraft.trim() || previewImport.isPending}
                  className="gap-2"
                >
                  {previewImport.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Gerar preview
                </Button>

                <Button
                  onClick={() => {
                    if (importProfileIds.length === 0 || !bulkPagesDraft.trim()) return
                    importConnections.mutate({
                      agent_profile_ids: importProfileIds,
                      lines: bulkPagesDraft,
                      group_links_lines: bulkGroupLinksDraft,
                    })
                  }}
                  disabled={importProfileIds.length === 0 || !bulkPagesDraft.trim() || importConnections.isPending}
                  className="gap-2"
                >
                  {importConnections.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  Importar lista
                </Button>
              </div>

              {importPreview && (
                <div className="space-y-3 rounded-xl border border-border bg-background p-3">
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <span className="rounded-full bg-sky-500/10 px-2.5 py-1 text-sky-600">
                      {importPreview.summary.valid} paginas x {importPreview.summary.profiles} perfis = {importPreview.summary.valid * importPreview.summary.profiles} combinacoes
                    </span>
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-primary">
                      {importPreview.summary.valid} paginas validas
                    </span>
                    <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-emerald-600">
                      {importPreview.summary.new_pairs} vinculos novos
                    </span>
                    <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-amber-600">
                      {importPreview.summary.duplicate_pairs} duplicados
                    </span>
                    {!!importPreview.summary.group_links_matched && (
                      <span className="rounded-full bg-fuchsia-500/10 px-2.5 py-1 text-fuchsia-600">
                        {importPreview.summary.group_links_matched} grupos em massa
                      </span>
                    )}
                    {importPreview.summary.ignored > 0 && (
                      <span className="rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                        {importPreview.summary.ignored} ignoradas
                      </span>
                    )}
                  </div>

                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {importPreview.entries.map((entry, index) => (
                      <div key={`${entry.fb_page_name}-${entry.fb_page_id ?? index}`} className="rounded-lg border border-border p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-foreground">{entry.fb_page_name}</p>
                          {entry.fb_page_id && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                              ID {entry.fb_page_id}
                            </span>
                          )}
                          {!!entry.groups_count && (
                            <span className="rounded-full bg-fuchsia-500/10 px-2 py-0.5 text-[10px] text-fuchsia-600">
                              {entry.groups_count} grupos
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {entry.profiles.map((profile) => (
                            <span
                              key={`${entry.fb_page_name}-${profile.agent_profile_id}`}
                              className={`rounded-full px-2 py-1 text-[10px] ${
                                profile.status === 'new'
                                  ? 'bg-emerald-500/10 text-emerald-600'
                                  : 'bg-amber-500/10 text-amber-600'
                              }`}
                            >
                              {profile.profile_name} · {profile.status === 'new' ? 'novo' : 'ja existe'}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManualPageModalOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
